import { SkillResource, SkillResourceType } from './skills.types';

/** Empty-folder marker. The skill API stores files, not directories. */
export const FOLDER_PLACEHOLDER_NAME = '.gitkeep';

const SCRIPT_EXTENSIONS = new Set(['py']);
const REFERENCE_EXTENSIONS = new Set(['md', 'txt', 'csv', 'json', 'html', 'xsd', 'xml']);

export const ALLOWED_SKILL_FILE_HINT = 'py, md, txt, csv, json, html, xml, xsd';

export function skillResourceType(fileName: string): SkillResourceType | null {
  const name = fileName.trim();
  if (name.toLowerCase() === FOLDER_PLACEHOLDER_NAME) {
    return 'reference';
  }
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  if (SCRIPT_EXTENSIONS.has(ext)) {
    return 'script';
  }
  if (REFERENCE_EXTENSIONS.has(ext)) {
    return 'reference';
  }
  return null;
}

export function skillContentType(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'py':
      return 'text/x-python';
    case 'json':
      return 'application/json';
    case 'html':
      return 'text/html';
    case 'csv':
      return 'text/csv';
    case 'xml':
    case 'xsd':
      return 'application/xml';
    case 'md':
      return 'text/markdown';
    default:
      return 'text/plain';
  }
}

export function skillResourceLanguage(type: SkillResourceType, fileName: string): string | null {
  if (type === 'script') {
    return 'python';
  }
  return fileName.split('.').pop()?.toLowerCase() || null;
}

export function folderPlaceholderPath(folder: string): string {
  const clean = folder.replace(/\/+$|^\/+/g, '');
  return clean ? `${clean}/${FOLDER_PLACEHOLDER_NAME}` : FOLDER_PLACEHOLDER_NAME;
}

export function withoutFolderPlaceholder<T extends { path: string }>(resources: T[], folder: string): T[] {
  const placeholder = folderPlaceholderPath(folder);
  return resources.filter((resource) => resource.path !== placeholder);
}

export function makeFolderPlaceholder(folder: string): SkillResource {
  const path = folderPlaceholderPath(folder);
  return {
    type: 'reference',
    path,
    fileId: `file-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    fileName: FOLDER_PLACEHOLDER_NAME,
    size: 1,
    contentType: 'text/plain',
    content: ' ',
    language: null,
  };
}

export type SkillPathItem = {
  name: string;
  path: string;
};

export type SkillFolderNode<T extends SkillPathItem = SkillPathItem> = {
  name: string;
  path: string;
  files: T[];
  folders: SkillFolderNode<T>[];
};

const DEFAULT_FOLDER_ORDER = ['references', 'scripts'];

export function isFolderPlaceholderPath(path: string): boolean {
  return path === FOLDER_PLACEHOLDER_NAME || path.endsWith(`/${FOLDER_PLACEHOLDER_NAME}`);
}

function addAncestorFolderPaths(folders: Set<string>, folderPath: string) {
  const parts = folderPath.split('/').filter(Boolean);
  for (let i = 1; i <= parts.length; i++) {
    folders.add(parts.slice(0, i).join('/'));
  }
}

function compareFolderNames(a: string, b: string, folderOrder: string[]) {
  const ai = folderOrder.indexOf(a);
  const bi = folderOrder.indexOf(b);
  return (ai === -1 ? folderOrder.length : ai) - (bi === -1 ? folderOrder.length : bi) || a.localeCompare(b);
}

export function buildSkillFolderTree<T extends SkillPathItem>(
  files: T[],
  folderOrder: string[] = DEFAULT_FOLDER_ORDER
): { rootFiles: T[]; folders: SkillFolderNode<T>[] } {
  const rootFiles: T[] = [];
  const folderPaths = new Set<string>();
  const filesByFolder = new Map<string, T[]>();

  for (const file of files) {
    if (isFolderPlaceholderPath(file.path)) {
      const folder = file.path === FOLDER_PLACEHOLDER_NAME
        ? ''
        : file.path.slice(0, -(FOLDER_PLACEHOLDER_NAME.length + 1));
      if (folder) {
        addAncestorFolderPaths(folderPaths, folder);
      }
      continue;
    }

    if (!file.path.includes('/')) {
      rootFiles.push(file);
      continue;
    }

    const parent = file.path.slice(0, file.path.lastIndexOf('/'));
    addAncestorFolderPaths(folderPaths, parent);
    const siblings = filesByFolder.get(parent) ?? [];
    siblings.push(file);
    filesByFolder.set(parent, siblings);
  }

  const nodes = new Map<string, SkillFolderNode<T>>();
  for (const path of folderPaths) {
    nodes.set(path, {
      name: path.slice(path.lastIndexOf('/') + 1),
      path,
      files: filesByFolder.get(path) ?? [],
      folders: [],
    });
  }

  const topLevel: SkillFolderNode<T>[] = [];
  for (const path of folderPaths) {
    const node = nodes.get(path);
    if (!node) continue;
    const parentPath = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
    const parent = parentPath ? nodes.get(parentPath) : undefined;
    if (parent) {
      parent.folders.push(node);
    } else {
      topLevel.push(node);
    }
  }

  const sortNodes = (list: SkillFolderNode<T>[], isTopLevel: boolean) => {
    list.sort((a, b) =>
      isTopLevel ? compareFolderNames(a.name, b.name, folderOrder) : a.name.localeCompare(b.name)
    );
    for (const node of list) {
      sortNodes(node.folders, false);
    }
  };
  sortNodes(topLevel, true);

  return { rootFiles, folders: topLevel };
}

export function collectFolderPaths<T extends SkillPathItem>(folders: SkillFolderNode<T>[]): string[] {
  const paths: string[] = [];
  const walk = (nodes: SkillFolderNode<T>[]) => {
    for (const node of nodes) {
      paths.push(node.path);
      walk(node.folders);
    }
  };
  walk(folders);
  return paths;
}
